import React, { FC, Children, createContext, useCallback, useContext } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { webUrl } from '@/url';
import { Img } from '@/ui/img';
import { Empty, message, Tooltip, Modal } from 'antd';
import { ZoomInOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import { stop } from '@@/utils';
import { useModal } from '@@/react-hooks';
import { OfferForVerification, SimilarProduct, SpuForVerification } from '@/interface/label';
import { SpuCombineResult } from '@/interface/label';
import { ellipsisOverflow } from '@/util/format';
import { DraggableTypes } from '@/const';
import * as apiService from '@/service';
import styles from './index.module.less';
import commonStyles from '../../common.module.less';
import { Gender } from '@reversible/schema';

/**
 * spu 选择器
 * @param param0
 * @returns
 */
export const ItemSelect: FC = ({ children }) =>
  Children.count(children) ? (
    <ul className={styles.item_select}>{children}</ul>
  ) : (
    <Empty className={commonStyles.empty} description={children || '没有可选项目'} />
  );

const combineContext = createContext<(spu0: SimilarProduct, spu1: SimilarProduct) => void>(null);

interface SelectableItemProps<T> {
  data: T;
  cols: number;
  checked?: boolean;
  onClick?(): void;
  onZoom?(): void;
}

export const SpuSelectableItem: FC<SelectableItemProps<SimilarProduct>> = ({
  data,
  cols,
  checked = false,
  onClick,
  onZoom,
  children,
}) => {
  const onCombine = useContext(combineContext);
  const draggable = Boolean(onCombine && data.id);

  const [{ isDragging }, dragRef, dragPreviewRef] = useDrag(
    {
      type: DraggableTypes.SPU_ITEM,
      item: data,
      collect: monitor => ({
        isDragging: monitor.isDragging(),
      }),
      canDrag: draggable,
    },
    [data, draggable]
  );

  const [{ isOver }, dropRef] = useDrop(
    () => ({
      accept: DraggableTypes.SPU_ITEM,
      collect: monitor => ({
        isOver: monitor.isOver(),
      }),
      canDrop: (data: SimilarProduct) => draggable && id !== data.id,
      drop: (spuDropped: SimilarProduct) => onCombine(data, spuDropped),
    }),
    [onCombine, draggable]
  );

  const { name, image, id, currency, price, color } = data;

  return (
    <li
      className={classNames(styles.selectable_item, {
        [styles.selectable_item_selected]: checked,
        [styles.selectable_item_draggover]: !isDragging && draggable && isOver,
        [styles.selectable_item_dragging]: isDragging,
      })}
      style={{
        flexBasis: `${Math.floor(100 / cols)}%`,
      }}
      ref={dropRef}
      onClick={onClick ? () => onClick() : undefined}
    >
      <div ref={dragRef}>
        <div className={styles.img_wrapper} ref={dragPreviewRef}>
          <Img className={styles.img} src={image} alt={name} />
        </div>
        <footer className={styles.footer}>
          {children}
          <div className={styles.info}>
            <Tooltip title={name}>
              <a target="_blank" className={styles.name} href={webUrl.product(data)} onClick={stop()}>
                {ellipsisOverflow(name, 60)}
              </a>
            </Tooltip>
            {color ? <h5 className={styles.info_item}>{color}</h5> : null}
            {currency ? (
              <h5 className={styles.info_item}>
                {Math.ceil(price)} {currency}
              </h5>
            ) : null}
          </div>
        </footer>
      </div>
      {onZoom && id ? (
        <a className={styles.zoom_btn} onClick={stop(() => onZoom())}>
          <ZoomInOutlined />
        </a>
      ) : null}
    </li>
  );
};

export const OfferSelectableItem: FC<SelectableItemProps<OfferForVerification> & {
  spu: SpuForVerification;
}> = ({
  spu,
  data: { imgUrl, offerName, offerId },
  cols,
  checked,
  onClick,
  onZoom,
  children,
}) => (
    <li
      className={classNames(styles.selectable_item, {
        [styles.selectable_item_selected]: checked,
      })}
      style={{
        flexBasis: `${Math.floor(100 / cols)}%`,
      }}
      onClick={() => onClick()}
    >
      <div className={styles.img_wrapper}>
        <Img className={styles.img} src={imgUrl} alt="Story" />
      </div>
      <footer className={styles.footer}>
        {children}
        <div className={styles.info}>
          <Tooltip title={offerName}>
            <a target="_blank" href={webUrl.productDetail({
              id: spu.spuId,
              gender: Gender.Women,
              name: spu.name,
              brand: spu.brand
            }, {
              offerId
            })} onClick={stop()} className={styles.name}>{offerName}</a>
          </Tooltip>
        </div>
      </footer>
      {onZoom ? (
        <a className={styles.zoom_btn} onClick={stop(() => onZoom())}>
          <ZoomInOutlined />
        </a>
      ) : null}
    </li>
  );

export const CombineProvider: FC<{
  source: string;
  onCombineFinish(combineResult: SpuCombineResult): void;
}> = ({ onCombineFinish, source, children }) => {
  const [combineModalState, openCombineModal, closeCombineModal] = useModal<{
    spu0: SimilarProduct;
    spu1: SimilarProduct;
  }>(null);

  const onRequestCombine = useCallback(async () => {
    const closeMessage = message.loading('正在合并商品', 0);
    try {
      const {
        data: { spu0, spu1 },
      } = combineModalState;
      const { data } = await apiService.postCombineProduct({
        spuId0: spu0.id,
        spuId1: spu1.id,
        source,
      });
      closeCombineModal();
      onCombineFinish(data);
    } catch (e) {
      message.error(e?.msg || '未知服务错误');
    }
    closeMessage();
  }, [combineModalState, onCombineFinish, source]);

  const onCombine = useCallback(
    (spu0: SimilarProduct, spu1: SimilarProduct) =>
      openCombineModal({
        spu0,
        spu1,
      }),
    [openCombineModal]
  );

  return (
    <>
      <combineContext.Provider value={onCombine}>{children}</combineContext.Provider>
      <Modal
        visible={combineModalState.visible}
        onCancel={closeCombineModal}
        onOk={onRequestCombine}
        okText="确定合并"
        cancelText="取消"
        title="确定要合并以下商品吗？"
        width="560px"
      >
        {combineModalState.data ? (
          <div className={styles.combine_modal_content}>
            <SpuSelectableItem data={combineModalState.data.spu0} cols={2} />
            <SpuSelectableItem data={combineModalState.data.spu1} cols={2} />
          </div>
        ) : null}
      </Modal>
    </>
  );
};
